import React, { useEffect, useState } from "react";
import { Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { UserDetailsWrapper } from "./styled";
import {
  BackButton,
  Box,
  ButtonStyled,
  LazyImage,
  SpanStyled,
  TextStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import Breadcrumbs from "../../../components/admin/Breadcrumbs/Breadcrumbs";
import { ContentWrapper } from "../../../components/styled/styled";
import { userDtlPermissionColumns } from "../../../helpers/adminConstants/TableConstants";
import { StyledTable } from "./components/UserPermissions";
import { userPermissionsRows } from "../../../helpers/dummyConstants";
import {
  GreenSuccessCheck,
  RedCancelIcon,
  UserPlaceholder,
} from "../../../components/styled/AllImages";
import UsersApi from "../../../services/api/usersApi";
import { toast } from "react-toastify";

const UserDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [userDetails, setUserDetails] = useState({});
  const [userInfoFields, setUserInfoFields] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [permissions, setPermissions] = useState({});

  const getUserInfo = async () => {
    try {
      setDataLoading(true);
      const { data, code } = await UsersApi.getUserInfo(id);

      if (code === 200) {
        const updatedUserDetails = {
          ...data,
          id_number: data?.user_detail?.id_number ?? "N/A",
        };
        setUserDetails(updatedUserDetails);

        setUserInfoFields([
          {
            label: "job_title",
            value: data.user_detail?.job_title ?? "N/A",
          },
          {
            label: "email",
            value: data.email ?? "N/A",
          },
          {
            label: "phone",
            value: data.phone ?? "N/A",
          },
          {
            label: "additional_info",
            value: data.user_detail?.additional_information ?? "N/A",
          },
        ]);

        const grouped = data.user_permissions.reduce((acc, perm) => {
          const typeId = Number(perm.permission_type_id);
          const operationId = Number(perm.permission_operation_id);
          if (!acc[typeId]) acc[typeId] = [];
          acc[typeId].push(operationId);
          return acc;
        }, {});
        setPermissions(grouped);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    getUserInfo();
  }, []);

  return (
    <UserDetailsWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("view_user")}
            onClick={() => navigate("/users")}
          />
          <Breadcrumbs />
        </Box>
      </Box>
      <ContentWrapper>
        {dataLoading ? (
          <ThemeLoader />
        ) : (
          <Box className="d-flex flex-wrap">
            <Col md={4} sm={12} className="user-info-box col-12">
              <Box
                style={{
                  backgroundColor: "var(--lightGrayBg)",
                  borderRadius: "15px",
                  margin: "10px",
                }}
                className="d-flex flex-column align-items-center justify-content-between gap-1"
              >
                <Box
                  style={{ padding: "10px" }}
                  className="d-flex align-items-center flex-column gap-2"
                >
                  <LazyImage
                    src={UserPlaceholder}
                    alt="User avatar"
                    className="rounded-circle"
                    style={{ width: "48px", height: "48px" }}
                  />
                  <Box className="d-flex flex-column align-items-center justify-content-center">
                    <TextStyled
                      color="var(--text)"
                      size="20px"
                      weight="900"
                      className="mb-0"
                    >
                      {userDetails.name}
                    </TextStyled>
                    <SpanStyled
                      size="16px"
                      weight="400"
                      color="var(--text2)"
                      className="mb-0"
                    >
                      {userDetails.id_number}
                    </SpanStyled>
                  </Box>
                </Box>

                <ButtonStyled
                  bg="var(--themeColor)"
                  className="mt-2"
                  label={t("edit_user")}
                  onClick={() => navigate(`/users/edit-user/${id}`)}
                />

                <Box className="w-100">
                  {userInfoFields.map((field, index) => (
                    <Box
                      key={index}
                      style={{
                        backgroundColor: "var(--white)",
                        borderRadius: "15px",
                        margin: "10px 0 0 0",
                        padding: "10px",
                        width: "100%",
                      }}
                      className={`d-flex ${
                        field.label === "additional_info"
                          ? "align-items-start text-wrap justify-content-between"
                          : "align-items-center justify-content-between"
                      }`}
                    >
                      <Col sm={4} className="col-4">
                        <SpanStyled color="var(--text2)">
                          {t(field.label)}
                        </SpanStyled>
                      </Col>

                      <Box
                        style={{
                          direction: "ltr",
                          wordBreak: "break-word",
                          width: "fit-content",
                        }}
                      >
                        {field.value}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Col>

            <Col md={8} sm={12} className="p-0 col-12 overflow-x-auto">
              <StyledTable className="border-0">
                <thead>
                  <tr>
                    {userDtlPermissionColumns.map((col, index) => (
                      <th
                        key={index}
                        className={`${col.title === "" && "title-column"}`}
                      >
                        <SpanStyled
                          display="block"
                          width="150px"
                          size="16px"
                          color="var(--text)"
                          weight="bold"
                          padding="10px 20px"
                          bg="var(--lightGrayBg)"
                          radius="12px"
                        >
                          {t(col.title)}
                        </SpanStyled>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {userPermissionsRows.map((row, rowIndex) => {
                    const permissionIds = Object.entries(row)
                      .filter(
                        ([key]) =>
                          key !== "module_name" && key !== "id" && key !== "all"
                      )
                      .map(([_, value]) => value);

                    const typePermissions = permissions[row.id] || [];
                    const hasAllPermissions =
                      permissionIds.length > 0 &&
                      permissionIds.every((permId) =>
                        typePermissions.includes(permId)
                      );

                    return (
                      <tr key={rowIndex}>
                        {userDtlPermissionColumns.map((col, colIndex) => {
                          const colName = col.name;

                          if (colName === "module_name") {
                            return (
                              <td key={colIndex}>
                                <SpanStyled
                                  display="block"
                                  width="150px"
                                  padding="17px 20px"
                                  bg="var(--lightGrayBg)"
                                  radius="12px"
                                  size="16px"
                                  color="var(--text)"
                                >
                                  {t(row[colName])}
                                </SpanStyled>
                              </td>
                            );
                          }
                          if (colName === "all") {
                            return (
                              <td key={colIndex}>
                                <Box className="d-flex justify-content-center">
                                  <SpanStyled
                                    display="block"
                                    width="150px"
                                    padding="7px 20px"
                                    bg={
                                      hasAllPermissions
                                        ? "var(--lightGrayBg)"
                                        : "transparent"
                                    }
                                    radius="12px"
                                    size="32px"
                                    color="var(--text)"
                                    border={
                                      !hasAllPermissions
                                        ? "var(--borderTwo)"
                                        : "none"
                                    }
                                  >
                                    {hasAllPermissions ? (
                                      <GreenSuccessCheck />
                                    ) : (
                                      <RedCancelIcon />
                                    )}
                                  </SpanStyled>
                                </Box>
                              </td>
                            );
                          }
                          const permissionId = row[colName];
                          const hasPermission =
                            typePermissions.includes(permissionId);

                          return (
                            <td key={colIndex}>
                              <Box className="d-flex justify-content-center">
                                <SpanStyled
                                  display="block"
                                  width="150px"
                                  padding="7px 20px"
                                  bg={
                                    hasPermission
                                      ? "var(--lightGrayBg)"
                                      : "transparent"
                                  }
                                  radius="12px"
                                  size="32px"
                                  color="var(--text)"
                                  border={
                                    !hasPermission ? "var(--borderTwo)" : "none"
                                  }
                                >
                                  {hasPermission ? (
                                    <GreenSuccessCheck />
                                  ) : (
                                    <RedCancelIcon />
                                  )}
                                </SpanStyled>
                              </Box>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </StyledTable>
            </Col>
          </Box>
        )}
      </ContentWrapper>
    </UserDetailsWrapper>
  );
};

export default UserDetails;
